/**
 * GALATEA SIGNATURE - DESIGN TOKENS SOBERANOS (P0)
 * Fonte de verdade única para cores, superfícies, densidades e elevamentos.
 * Prescrição visual PO Artur Saldanha: Presença real de Azul no modo LIGHT (Sem brancos puros).
 * 4 SABORES OFICIAIS: SIGNATURE, EXECUTIVE, PROCESS, MINIMAL (LIGHT & DARK para cada um)
 */

export type Flavor = 'SIGNATURE' | 'EXECUTIVE' | 'PROCESS' | 'MINIMAL';
export type Mode = 'light' | 'dark';
/** Preferência de luminosidade escolhida pelo usuário. 'auto' segue o sistema operacional. */
export type ModePreference = Mode | 'auto';
export type Density = 'comfortable' | 'compact';
export type Archetype = 'CONTROL' | 'FORM' | 'GRID' | 'FLOW';

export interface ColorTokens {
  bgApp: string;            // APP_BACKGROUND
  bgWorkspace: string;      // WORKSPACE
  bgSurface: string;        // SURFACE dos cards/tabelas
  bgSurfaceHover: string;   // SURFACE_RAISED / HOVER
  bgHeader: string;         // HEADER
  bgSidebar: string;        // SIDEBAR
  sidebarHover: string;     // SIDEBAR_HOVER
  sidebarSelected: string;  // SIDEBAR_SELECTED
  bgNav: string;            // Compatibilidade (#1B2B42)
  border: string;           // BORDER
  divider: string;          // DIVIDER
  borderActive: string;     // PRIMARY / ACTIVE
  primary: string;          // PRIMARY
  primaryHover: string;     // HOVER PRIMARY
  primaryLight: string;     // PRIMARY_SOFT
  accentIA: string;         // AI (#8B5CF6 / Roxo exclusivo)
  accentIALight: string;    // AI_SOFT
  textPrimary: string;      // TEXT_PRIMARY
  textSecondary: string;    // TEXT_SECONDARY
  textMuted: string;        // TEXT_MUTED
  success: string;          // Verde semântico
  warning: string;          // Âmbar semântico
  danger: string;           // Vermelho semântico
}

export interface DensityTokens {
  headerHeightPx: number;
  sidebarWidthPx: number;
  actionBarHeightPx: number;
  inputMinHeightPx: number;
  tableRowHeightPx: number;
  paddingMainPx: number;
  cardPaddingPx: number;
  gapPx: number;
  fontSizeBasePx: number;
}

export const DENSITY_TOKENS: Record<Density, DensityTokens> = {
  comfortable: {
    headerHeightPx: 56,
    sidebarWidthPx: 256,
    actionBarHeightPx: 85,
    inputMinHeightPx: 40,
    tableRowHeightPx: 48,
    paddingMainPx: 24,
    cardPaddingPx: 24,
    gapPx: 20,
    fontSizeBasePx: 14,
  },
  compact: {
    headerHeightPx: 48,
    sidebarWidthPx: 240,
    actionBarHeightPx: 70,
    inputMinHeightPx: 32,
    tableRowHeightPx: 38,
    paddingMainPx: 16,
    cardPaddingPx: 16,
    gapPx: 12,
    fontSizeBasePx: 13,
  },
};

export const COLOR_TOKENS: Record<Flavor, Record<Mode, ColorTokens>> = {
  // 1. SABOR SIGNATURE (Sabor Padrão Corporativo Contemporâneo)
  SIGNATURE: {
    light: {
      bgApp: '#D8E4EE',            // APP_BACKGROUND azul corporativo
      bgWorkspace: '#E1EAF2',      // WORKSPACE azul suave
      bgSurface: '#E8EFF5',        // SURFACE dos cards
      bgSurfaceHover: '#EDF3F7',   // SURFACE_RAISED
      bgHeader: '#17263B',         // HEADER Navy clássico
      bgSidebar: '#1B2B42',        // SIDEBAR Navy
      sidebarHover: '#253A56',
      sidebarSelected: '#294A70',
      bgNav: '#1B2B42',
      border: '#BCCEDC',
      divider: '#CAD8E3',
      borderActive: '#2474A8',
      primary: '#2474A8',          // Azul Galatea corporativo
      primaryHover: '#1C5C86',
      primaryLight: '#C5DDEA',
      accentIA: '#8B5CF6',         // Roxo Galatea IA
      accentIALight: '#DED4F7',
      textPrimary: '#172A3E',
      textSecondary: '#526A80',
      textMuted: '#74899C',
      success: '#10B981',
      warning: '#F59E0B',
      danger: '#EF4444',
    },
    dark: {
      bgApp: '#0B1726',
      bgWorkspace: '#0F1E30',
      bgSurface: '#152232',
      bgSurfaceHover: '#1E2E42',
      bgHeader: '#0B1726',
      bgSidebar: '#09111E',
      sidebarHover: '#17273C',
      sidebarSelected: '#294A70',
      bgNav: '#09111E',
      border: '#2A3C52',
      divider: '#1E2E42',
      borderActive: '#38BDF8',
      primary: '#38BDF8',
      primaryHover: '#7DD3FC',
      primaryLight: '#075985',
      accentIA: '#C084FC',
      accentIALight: '#3B0764',
      textPrimary: '#F1F5F9',
      textSecondary: '#94A3B8',
      textMuted: '#64748B',
      success: '#34D399',
      warning: '#FBBF24',
      danger: '#F87171',
    },
  },

  // 2. SABOR EXECUTIVE (Gestão, Direção, Análise & KPIs)
  EXECUTIVE: {
    light: {
      bgApp: '#CFD9E8',            // Azul aço profundo
      bgWorkspace: '#D8E3F0',
      bgSurface: '#E2EBF5',
      bgSurfaceHover: '#EBF1F8',
      bgHeader: '#0F1E36',         // Header Navy escuro de alta autoridade
      bgSidebar: '#12223D',
      sidebarHover: '#1A3052',
      sidebarSelected: '#1E3A5F',
      bgNav: '#12223D',
      border: '#94A3B8',           // Maior contraste de borda
      divider: '#CBD5E1',
      borderActive: '#1D4ED8',
      primary: '#1D4ED8',          // Royal Executive Blue
      primaryHover: '#1E40AF',
      primaryLight: '#BFDBFE',
      accentIA: '#7C3AED',
      accentIALight: '#DDD6FE',
      textPrimary: '#0F172A',      // Contraste elevado de texto
      textSecondary: '#334155',
      textMuted: '#64748B',
      success: '#059669',
      warning: '#D97706',
      danger: '#DC2626',
    },
    dark: {
      bgApp: '#070C18',
      bgWorkspace: '#0B1324',
      bgSurface: '#101B30',
      bgSurfaceHover: '#172742',
      bgHeader: '#050810',
      bgSidebar: '#03050B',
      sidebarHover: '#122038',
      sidebarSelected: '#1D4ED8',
      bgNav: '#03050B',
      border: '#1E293B',
      divider: '#0F172A',
      borderActive: '#60A5FA',
      primary: '#60A5FA',
      primaryHover: '#93C5FD',
      primaryLight: '#1E3A8A',
      accentIA: '#A78BFA',
      accentIALight: '#4C1D95',
      textPrimary: '#F8FAFC',
      textSecondary: '#94A3B8',
      textMuted: '#64748B',
      success: '#34D399',
      warning: '#FBBF24',
      danger: '#F87171',
    },
  },

  // 3. SABOR PROCESS (Orientado a Implantação, Etapas & Fluxos Guiados)
  PROCESS: {
    light: {
      bgApp: '#D3E6E7',            // Teal/Cyan corporativo guiado
      bgWorkspace: '#DCEEEE',
      bgSurface: '#E6F4F4',
      bgSurfaceHover: '#EEF9F9',
      bgHeader: '#0F2C33',         // Slate Cyan escuro
      bgSidebar: '#143842',
      sidebarHover: '#1C4A56',
      sidebarSelected: '#225B6A',
      bgNav: '#143842',
      border: '#93C5CC',
      divider: '#BCE2E6',
      borderActive: '#0284C7',
      primary: '#0284C7',          // Process Cyan/Sky Blue
      primaryHover: '#0369A1',
      primaryLight: '#BAE6FD',
      accentIA: '#9333EA',
      accentIALight: '#F3E8FF',
      textPrimary: '#0C2A30',
      textSecondary: '#2D5962',
      textMuted: '#527D86',
      success: '#10B981',
      warning: '#F59E0B',
      danger: '#EF4444',
    },
    dark: {
      bgApp: '#051419',
      bgWorkspace: '#081D24',
      bgSurface: '#0E2730',
      bgSurfaceHover: '#153542',
      bgHeader: '#030D10',
      bgSidebar: '#02080B',
      sidebarHover: '#112C36',
      sidebarSelected: '#0284C7',
      bgNav: '#02080B',
      border: '#154E60',
      divider: '#0C3744',
      borderActive: '#38BDF8',
      primary: '#38BDF8',
      primaryHover: '#7DD3FC',
      primaryLight: '#075985',
      accentIA: '#C084FC',
      accentIALight: '#581C87',
      textPrimary: '#F0FDFC',
      textSecondary: '#99F6E4',
      textMuted: '#5EEAD4',
      success: '#34D399',
      warning: '#FBBF24',
      danger: '#F87171',
    },
  },

  // 4. SABOR MINIMAL (Alta Densidade Operacional, Grids & Cadastro Extenso)
  MINIMAL: {
    light: {
      bgApp: '#D5DAE1',            // Slate neutro cirúrgico de alta precisão
      bgWorkspace: '#DFE4EC',
      bgSurface: '#E8ECF3',
      bgSurfaceHover: '#EFF3F8',
      bgHeader: '#1E293B',         // Graphite Slate
      bgSidebar: '#253347',
      sidebarHover: '#31425B',
      sidebarSelected: '#3B4E6B',
      bgNav: '#253347',
      border: '#94A3B8',           // Linhas finas bem definidas
      divider: '#CBD5E1',
      borderActive: '#2563EB',
      primary: '#2563EB',          // Precision Blue
      primaryHover: '#1D4ED8',
      primaryLight: '#DBEAFE',
      accentIA: '#7C3AED',
      accentIALight: '#EDE9FE',
      textPrimary: '#0F172A',
      textSecondary: '#334155',
      textMuted: '#64748B',
      success: '#059669',
      warning: '#D97706',
      danger: '#DC2626',
    },
    dark: {
      bgApp: '#0B0F17',
      bgWorkspace: '#101622',
      bgSurface: '#161F2E',
      bgSurfaceHover: '#1F2B3E',
      bgHeader: '#070A0F',
      bgSidebar: '#04060A',
      sidebarHover: '#182436',
      sidebarSelected: '#2563EB',
      bgNav: '#04060A',
      border: '#1E293B',
      divider: '#0F172A',
      borderActive: '#60A5FA',
      primary: '#60A5FA',
      primaryHover: '#93C5FD',
      primaryLight: '#1E3A8A',
      accentIA: '#A78BFA',
      accentIALight: '#4C1D95',
      textPrimary: '#F8FAFC',
      textSecondary: '#94A3B8',
      textMuted: '#64748B',
      success: '#34D399',
      warning: '#FBBF24',
      danger: '#F87171',
    },
  },
};
